// ------------------------------------------------------
// PrivacyPolicy.js
// ------------------------------------------------------

import React, { useEffect, useContext } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Helmet } from 'react-helmet';
import { AppContext } from './AppContext';

import Header from './Header';
import Footer from './Footer';

// ---------------------------------------------
// Policy Data Object
// ---------------------------------------------
const policyData = {
    lastUpdated: '03/01/2025',
    businessType: 'Sole Proprietorship',
    businessOwner: 'Mr. Anindya Chowdhury',
    contactEmail: 'connect@buzztobrand.com',
    website: 'https://www.buzztobrand.com',
};

export default function PrivacyPolicy() {
    const { settings } = useContext(AppContext);
    const brandName = settings?.brandName || 'Buzz To Brand';

    useEffect(() => {
        AOS.init({ once: true });
    }, []);

    const formattedDate = (() => {
        const [day, month, year] = policyData.lastUpdated.split('/');
        const dateObj = new Date(`${year}-${month}-${day}`);
        return dateObj.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    })();

    return (
        <>
            <Helmet>
                <title>Privacy Policy | {brandName}</title>
                <meta
                    name="description"
                    content={`Read the Privacy Policy of ${brandName} to understand how we collect, use, and protect your information.`}
                />
            </Helmet>

            <Header />

            <main
                className="p-6 lg:p-16 max-w-4xl mx-auto text-gray-800 leading-relaxed text-justify"
                data-aos="fade-up"
            >
                <h1 className="text-4xl font-bold mb-8 text-center">
                    Privacy Policy
                </h1>

                <p className="text-sm text-gray-500 mb-10 italic text-center">
                    Last Updated: {formattedDate}
                </p>

                {/* ---------------------- INTRO ---------------------- */}
                <section className="mb-10">
                    <p>
                        Welcome to <strong>{brandName}</strong> (“Business”, “we”, “us”, or
                        “our”). The website{' '}
                        <a
                            href={policyData.website}
                            className="text-indigo-600 hover:underline"
                        >
                            {policyData.website}
                        </a>{' '}
                        (“Website”) is owned and operated by{' '}
                        <strong>{policyData.businessOwner}</strong>, operating as a{' '}
                        {policyData.businessType} based in West Bengal, India.
                    </p>

                    <p className="mt-4">
                        We provide brand strategy, marketing consulting, design, and digital
                        services. This Privacy Policy explains how we collect, use, store,
                        and protect your information when you interact with our Website or
                        services. By using this Website or our services, you agree to this
                        Privacy Policy.
                    </p>
                </section>

                {/* ---------------------- INFORMATION ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        1. Information We Collect
                    </h2>
                    <p>
                        We may collect personal and non-personal information, including:
                    </p>
                    <ul className="list-disc ml-6 mt-3 space-y-2">
                        <li>
                            Personal details such as name, email address, phone number, and
                            company information.
                        </li>
                        <li>
                            Information voluntarily shared through forms, emails, messages,
                            or consultations.
                        </li>
                        <li>
                            Automatically collected data such as IP address, browser type,
                            device information, and usage behavior.
                        </li>
                        <li>
                            Cookies and similar technologies used to enhance user experience
                            and analyze Website traffic. You may manage or disable cookies
                            through your browser settings.
                        </li>
                    </ul>
                </section>

                {/* ---------------------- USE ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        2. How We Use Your Information
                    </h2>
                    <ul className="list-disc ml-6 mt-3 space-y-2">
                        <li>To provide, operate, and improve our services.</li>
                        <li>To respond to inquiries, requests, or communications.</li>
                        <li>
                            To send updates or newsletters only where you have opted in.
                        </li>
                        <li>
                            To analyze Website usage and improve performance and experience.
                        </li>
                        <li>To comply with applicable legal and regulatory requirements.</li>
                    </ul>
                </section>

                {/* ---------------------- SHARING ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        3. Sharing of Information
                    </h2>
                    <p>
                        We do not sell or rent your personal information. We may share
                        limited data only with:
                    </p>
                    <ul className="list-disc ml-6 mt-3 space-y-2">
                        <li>
                            Trusted service providers assisting with hosting, analytics,
                            communication, or operations, under confidentiality obligations.
                        </li>
                        <li>
                            Legal or regulatory authorities where disclosure is required by
                            law.
                        </li>
                        <li>
                            Successor entities in the event of a business restructuring or
                            transfer.
                        </li>
                    </ul>
                </section>

                {/* ---------------------- SECURITY ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        4. Data Security and Retention
                    </h2>
                    <p>
                        We use reasonable administrative, technical, and organizational
                        measures to protect your information from unauthorized access,
                        misuse, or loss. However, no method of transmission or storage is
                        completely secure.
                    </p>
                    <p className="mt-4">
                        We retain personal information only for as long as necessary to
                        fulfill the purposes outlined in this Privacy Policy or as required
                        by applicable law.
                    </p>
                </section>

                {/* ---------------------- RIGHTS ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        5. Your Rights
                    </h2>
                    <p>
                        You may request access to, correction of, or deletion of your
                        personal information. You may also withdraw consent for
                        communications at any time by writing to{' '}
                        <a
                            href={`mailto:${policyData.contactEmail}`}
                            className="text-indigo-600 hover:underline"
                        >
                            {policyData.contactEmail}
                        </a>
                        .
                    </p>
                </section>

                {/* ---------------------- THIRD PARTY ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        6. Third-Party Links
                    </h2>
                    <p>
                        Our Website may contain links to third-party websites or services.
                        We are not responsible for their content or privacy practices.
                        Please review their policies before engaging with them.
                    </p>
                </section>

                {/* ---------------------- CHILDREN ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        7. Children’s Privacy
                    </h2>
                    <p>
                        Our Website and services are not intended for individuals under
                        eighteen (18) years of age. We do not knowingly collect personal
                        information from minors.
                    </p>
                </section>

                {/* ---------------------- UPDATES ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        8. Updates and Governing Law
                    </h2>
                    <p>
                        We may update this Privacy Policy from time to time. Any changes
                        will be reflected by updating the “Last Updated” date above.
                        Continued use of the Website or services constitutes acceptance of
                        the revised policy.
                    </p>
                    <p className="mt-4">
                        This Privacy Policy shall be governed by the laws of India.
                    </p>
                </section>

                {/* ---------------------- CONTACT ---------------------- */}
                <section className="mb-10">
                    <h2 className="text-2xl font-semibold mb-3">
                        9. Contact Us
                    </h2>
                    <p>
                        For questions or concerns regarding this Privacy Policy, please
                        contact us at{' '}
                        <a
                            href={`mailto:${policyData.contactEmail}`}
                            className="text-indigo-600 hover:underline"
                        >
                            {policyData.contactEmail}
                        </a>
                        .
                    </p>
                </section>

                <p className="text-center mt-12 font-medium">
                    YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY
                    AND AGREE TO BE BOUND BY ITS TERMS.
                </p>
            </main>

            <Footer />
        </>
    );
}
